import React, { useRef, useState, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import { ConfirmActionPopup } from '../../../../../../components/PopUps';
import { EventAttachmentMediaTypes } from '../../../../../../utils/eventConstants';
import { isOrganizer } from '../../../../../../utils/events';
import { getEventAttachments, deleteEventAttachment, sortEventAttachments } from '../../../../../../actions/event-profile-actions';
import AttachmentItem from './AttachmentItem';

import styles from './attachments.scss';

const DROPPABLE_ID = 'attachments-droppable';

const Attachments = () => {
    const { attachments, eventId, eventData } = useSelector(({ eventProfile }) => {
        const { attachments } = eventProfile || {};
        const eventId = eventProfile.event?.data?.id;
        const eventData = eventProfile.event?.data;
        return { attachments, eventId, eventData };
    }, shallowEqual);
    const dispatch = useDispatch();

    const downloadLinkRef = useRef(null);
    const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
    const [toDeleteAttachment, setToDeleteAttachment] = useState(null);

    const isEventOrganizer = isOrganizer(eventData);

    useEffect(() => {
        if (eventId) {
            dispatch(getEventAttachments(eventId));
        }
    }, [eventId]);

    const handleAttachmentClick = (attachment) => {
        if (attachment.attachmentMediaType === EventAttachmentMediaTypes.LINK) {
            const win = window.open(attachment.srcUrl, '_blank');
            win.focus();
        } else if (downloadLinkRef.current) {
            downloadLinkRef.current.href = `/api/v1/tms/events/${eventId}/file/${attachment.fileId}`;
            downloadLinkRef.current.click();
        }
    };

    const handleDeleteAttachmentClick = (attachment) => {
        setToDeleteAttachment(attachment);
        setShowDeleteConfirmPopup(true);
    };

    const deleteAttachment = () => {
        dispatch(deleteEventAttachment(eventId, toDeleteAttachment.fileId));
        setToDeleteAttachment(null);
        setShowDeleteConfirmPopup(false);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) {
            return;
        }

        const reordered = [...attachments];
        const [moved] = reordered.splice(sourceIndex, 1);
        reordered.splice(destinationIndex, 0, moved);

        dispatch(sortEventAttachments(eventId, reordered));
    };

    const hasAttachments = attachments?.length > 0;

    return (
        <>
            {hasAttachments && (
                <>
                    <h6>Links/Attachments</h6>
                    {isEventOrganizer && (
                        <div className={styles.sortInstructions}>
                            Drag and drop to set order
                        </div>
                    )}
                    <div
                        data-test="attachments"
                        className={styles.attachments}
                    >
                        <a ref={downloadLinkRef} className="d-none" target="_blank" rel="noopener noreferrer" />
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId={DROPPABLE_ID}>
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {attachments.map((attachment, index) => (
                                            <AttachmentItem
                                                key={`attachment-${attachment.fileId}`}
                                                attachment={attachment}
                                                index={index}
                                                isDraggable={isEventOrganizer}
                                                onClickAttachment={handleAttachmentClick}
                                                onDeleteAttachment={handleDeleteAttachmentClick}
                                            />
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                </>
            )}
            {showDeleteConfirmPopup && (
                <ConfirmActionPopup
                    onCloseClick={() => {
                        setShowDeleteConfirmPopup(false);
                    }}
                    onCtaClick={deleteAttachment}
                    confirmationTitle="Delete Attachment"
                    confirmationMessage="Delete attachment?"
                />
            )}
        </>
    );
};
export default Attachments;
